import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../i18n";
import BottomNav from "../components/BottomNav";
import CategoryManager from "../components/CategoryManager";
import LanguagePickerDrawer from "../components/LanguagePickerDrawer";
import NotificationsToggle from "../components/NotificationsToggle";
import { Button } from "../components/ui/button";
import { Copy, LogOut, Users, Link2, FolderTree, ShieldCheck, Languages, Briefcase, Home as HomeIcon, Download } from "lucide-react";

export default function Settings() {
  const { user, logout } = useAuth();
  const { t, lang } = useT();
  const [hh, setHh] = useState(null);
  const [catManagerOpen, setCatManagerOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try { const r = await api.get("/households/me"); setHh(r.data); } catch (_e) { /* noop */ }
    })();
  }, []);

  const inviteUrl = hh?.invite_code
    ? `${window.location.origin}/?invite=${encodeURIComponent(hh.invite_code)}`
    : "";

  const copy = () => {
    if (!hh?.invite_code) return;
    navigator.clipboard?.writeText(hh.invite_code);
    toast.success(t("settings.toast.code_copied"));
  };

  const copyLink = () => {
    if (!inviteUrl) return;
    navigator.clipboard?.writeText(inviteUrl);
    toast.success(t("settings.toast.link_copied"));
  };

  const share = async () => {
    if (!hh?.invite_code) return;
    const text = "Niper — " + t("settings.share");
    const payload = { title: "Niper — Nihil Perditum", text, url: inviteUrl };
    if (navigator.share) {
      try { await navigator.share(payload); return; } catch (_e) { /* fallback */ }
    }
    copyLink();
  };

  const limits = user?.plan_limits || {};
  const usage = user?.usage || {};
  const planLabel =
    user?.is_super_admin ? t("settings.plan.unlimited")
    : user?.account_type === "private" ? t("settings.plan.private")
    : user?.account_type === "business" ? t("settings.plan.business")
    : t("settings.plan.unset");

  return (
    <div className="min-h-screen bg-white pb-32" data-testid="settings-page">
      <header className="pt-safe pt-10 px-6">
        <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-fresh-green-text">{t("settings.eyebrow")}</div>
        <h1 className="mt-2 font-heading text-4xl font-medium">{t("settings.title")}</h1>
      </header>

      <section className="px-6 mt-8">
        <div className="rounded-3xl bg-zinc-50 border border-zinc-100 p-6">
          <div className="text-[11px] font-bold uppercase tracking-widest text-zinc-500">{t("settings.pantry")}</div>
          <div className="mt-1 font-heading text-2xl font-medium">{hh?.name || "—"}</div>
          <div className="mt-6 text-[11px] font-bold uppercase tracking-widest text-zinc-500">{t("settings.invite_code")}</div>
          <div className="mt-1 flex items-center gap-3">
            <div className="font-mono text-3xl tracking-[0.35em] font-bold text-zinc-950" data-testid="invite-code-value">
              {hh?.invite_code || "……"}
            </div>
            <button onClick={copy} className="ml-auto w-10 h-10 rounded-full bg-white border border-zinc-200 flex items-center justify-center" data-testid="copy-invite-btn" aria-label={t("common.copy")}>
              <Copy className="w-4 h-4" />
            </button>
          </div>
          {inviteUrl && (
            <div className="mt-4 flex items-center gap-2 rounded-xl bg-white border border-zinc-200 px-3 py-2">
              <Link2 className="w-4 h-4 text-zinc-400 shrink-0" />
              <div className="flex-1 min-w-0 text-xs text-zinc-600 truncate font-mono" data-testid="invite-link-value">
                {inviteUrl}
              </div>
              <button onClick={copyLink} className="w-8 h-8 rounded-lg bg-zinc-100 flex items-center justify-center" data-testid="copy-invite-link-btn" aria-label={t("common.copy")}>
                <Copy className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          <Button onClick={share} className="mt-4 w-full h-12 rounded-xl bg-zinc-950 text-white" data-testid="share-invite-btn">
            {t("settings.share")}
          </Button>
        </div>

        {/* Plan / Usage */}
        <div className="mt-6 rounded-3xl bg-zinc-50 border border-zinc-100 p-5" data-testid="settings-plan-card">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-white border border-zinc-200 flex items-center justify-center">
              {user?.is_super_admin ? <ShieldCheck className="w-5 h-5 text-fresh-green-text" /> : user?.account_type === "business" ? <Briefcase className="w-5 h-5" /> : <HomeIcon className="w-5 h-5" />}
            </div>
            <div className="flex-1">
              <div className="text-[11px] font-bold uppercase tracking-widest text-zinc-500">{t("settings.plan.title")}</div>
              <div className="font-heading text-lg font-medium">{planLabel}</div>
            </div>
          </div>
          {limits && Object.keys(limits).length > 0 && (
            <div className="mt-4 grid grid-cols-3 gap-2">
              <UsageTile label={t("settings.usage.households")} now={usage.households_owned ?? 0} max={limits.max_households} testId="usage-households" />
              <UsageTile label={t("settings.usage.members")} now={usage.members_in_pantry ?? 0} max={limits.max_members_per_household} testId="usage-members" />
              <UsageTile label={t("settings.usage.items")} now={usage.items_in_pantry ?? 0} max={limits.max_items} testId="usage-items" />
            </div>
          )}
        </div>

        {/* PWA install button - always visible */}
        <InstallAppCta t={t} />

        {/* Push notifications toggle */}
        <NotificationsToggle />

        <button
          onClick={() => setLangOpen(true)}
          className="mt-4 w-full rounded-3xl bg-zinc-50 border border-zinc-100 p-5 flex items-center gap-4 text-left active:scale-[0.98] transition"
          data-testid="open-language-picker-btn"
        >
          <div className="w-11 h-11 rounded-2xl bg-zinc-950 text-white flex items-center justify-center">
            <Languages className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="font-heading text-base font-medium">{t("settings.lang.title")}</div>
            <div className="text-xs text-zinc-500 mt-0.5">{t("settings.lang.desc")}</div>
          </div>
          <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">{lang === "en" ? "EN-US" : "IT"}</span>
        </button>

        <button
          onClick={() => setCatManagerOpen(true)}
          className="mt-4 w-full rounded-3xl bg-zinc-50 border border-zinc-100 p-5 flex items-center gap-4 text-left active:scale-[0.98] transition"
          data-testid="open-category-manager-btn"
        >
          <div className="w-11 h-11 rounded-2xl bg-zinc-950 text-white flex items-center justify-center">
            <FolderTree className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="font-heading text-base font-medium">{t("settings.cat.title")}</div>
            <div className="text-xs text-zinc-500 mt-0.5">{t("settings.cat.desc")}</div>
          </div>
        </button>

        {user?.is_super_admin && (
          <a
            href="/admin"
            className="mt-4 w-full rounded-3xl bg-zinc-950 text-white p-5 flex items-center gap-4 active:scale-[0.98] transition"
            data-testid="settings-admin-link"
          >
            <div className="w-11 h-11 rounded-2xl bg-fresh-green text-black flex items-center justify-center">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="font-heading text-base font-medium">{t("settings.admin.title")}</div>
              <div className="text-xs text-white/70 mt-0.5">{t("settings.admin.desc")}</div>
            </div>
          </a>
        )}

        <div className="mt-8">
          <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-zinc-500 mb-3 flex items-center gap-2">
            <Users className="w-3.5 h-3.5" /> {t("settings.members")}
          </div>
          <div className="divide-y divide-zinc-100 border-t border-b border-zinc-100" data-testid="members-list">
            {(hh?.members || []).map((m) => (
              <div key={m.user_id} className="flex items-center gap-3 py-4">
                {m.picture ? <img src={m.picture} alt="" className="w-10 h-10 rounded-full" /> : <div className="w-10 h-10 rounded-full bg-zinc-200" />}
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{m.name}</div>
                  <div className="text-xs text-zinc-500 truncate">{m.email}</div>
                </div>
                {hh?.owner_id === m.user_id && (
                  <span className="text-[10px] tracking-widest font-bold text-fresh-green-text bg-fresh-green-soft px-2 py-1 rounded-full uppercase">
                    {t("settings.members.owner")}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        <Button onClick={logout} variant="secondary" className="mt-10 w-full h-12 rounded-xl bg-zinc-100 text-zinc-950" data-testid="logout-btn">
          <LogOut className="w-4 h-4 mr-2" /> {t("settings.signout")}
        </Button>
        <div className="text-center text-[11px] text-zinc-400 mt-6">
          {t("settings.signed_as", { email: user?.email })}
        </div>
      </section>

      <CategoryManager open={catManagerOpen} onOpenChange={setCatManagerOpen} />
      <LanguagePickerDrawer open={langOpen} onOpenChange={setLangOpen} />

      <BottomNav />
    </div>
  );
}

function InstallAppCta({ t }) {
  const [supported, setSupported] = React.useState(false);
  const [installed, setInstalled] = React.useState(false);
  const deferred = React.useRef(null);

  React.useEffect(() => {
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      window.navigator.standalone === true;
    if (isStandalone) { setInstalled(true); return undefined; }
    const onBefore = (e) => { e.preventDefault(); deferred.current = e; setSupported(true); };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const isIOS = /iPad|iPhone|iPod/.test(window.navigator.userAgent || "") && !window.MSStream;

  const install = async () => {
    const ev = deferred.current;
    if (!ev) return;
    try { ev.prompt(); await ev.userChoice; } catch (_e) { /* noop */ }
    deferred.current = null;
    setSupported(false);
  };

  if (installed) {
    return (
      <div className="mt-4 rounded-3xl bg-fresh-green-soft border border-fresh-green/30 p-4 flex items-center gap-3" data-testid="install-installed">
        <div className="w-10 h-10 rounded-xl bg-fresh-green text-black flex items-center justify-center">
          <Download className="w-4 h-4" />
        </div>
        <div className="text-sm font-medium text-fresh-green-text">{t("install.installed")}</div>
      </div>
    );
  }

  // Show CTA: native install on Android/Chrome, iOS instructions otherwise
  return (
    <div className="mt-4 rounded-3xl bg-zinc-950 text-white p-5" data-testid="install-cta">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-fresh-green text-black flex items-center justify-center">
          <Download className="w-5 h-5" strokeWidth={2.4} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading text-base font-medium">{t("install.title")}</div>
          <div className="text-xs text-white/70 mt-0.5">
            {supported ? t("install.desc.android") : isIOS ? t("install.desc.ios") : t("install.desc.fallback")}
          </div>
        </div>
      </div>
      {supported && (
        <button
          onClick={install}
          className="mt-4 w-full h-11 rounded-xl bg-fresh-green text-black font-bold text-sm active:scale-95"
          data-testid="install-cta-btn"
        >
          {t("install.btn")}
        </button>
      )}
    </div>
  );
}

function UsageTile({ label, now, max, testId }) {
  const unlimited = max === -1 || max == null;
  const pct = unlimited ? 0 : Math.min(100, Math.round((now / max) * 100));
  const danger = !unlimited && pct >= 90;
  const warn = !unlimited && pct >= 70 && pct < 90;
  const barClass = danger ? "bg-fresh-red" : warn ? "bg-fresh-yellow" : "bg-fresh-green";
  return (
    <div className="rounded-2xl bg-white border border-zinc-200 p-3" data-testid={testId}>
      <div className="font-heading text-lg font-semibold leading-none">
        <span>{now}</span>
        <span className="text-zinc-400 text-sm font-normal"> / {unlimited ? "∞" : max}</span>
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-zinc-100 overflow-hidden">
        <div className={`h-full ${unlimited ? "bg-zinc-300" : barClass}`} style={{ width: unlimited ? "100%" : `${pct}%` }} />
      </div>
      <div className="text-[10px] mt-2 text-zinc-500 uppercase tracking-wider font-bold leading-tight">{label}</div>
    </div>
  );
}
